import { useState, useEffect, useCallback, useRef } from 'react';
import * as Location from 'expo-location';
import { useMutation } from '@apollo/client';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { UPDATE_DRIVER_LOCATION } from '../gql/trip/mutation';
import {
  requestBackgroundPermissions,
  startBackgroundLocationTracking,
  stopBackgroundLocationTracking,
  isBackgroundLocationTrackingActive,
} from '../services/backgroundLocation';

const LOCATION_UPDATE_INTERVAL = 30000; // 30 seconds
const STORAGE_KEY_DISCLOSURE_ACCEPTED = '@location_disclosure_accepted';

export default function useLocationTracking(tripId, isTracking = false) {
  const [location, setLocation] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);
  const [isBackgroundTracking, setIsBackgroundTracking] = useState(false);
  const [isDisclosureVisible, setIsDisclosureVisible] = useState(false);
  const locationSubscription = useRef(null);
  const disclosureDecision = useRef(null);

  const [updateDriverLocation] = useMutation(UPDATE_DRIVER_LOCATION, {
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error updating driver location:', error);
    },
  });

  const ensureDisclosureAccepted = useCallback(async () => {
    if (Platform.OS === 'web') return true;

    const alreadyAccepted = await AsyncStorage.getItem(STORAGE_KEY_DISCLOSURE_ACCEPTED);
    if (alreadyAccepted === 'true') return true;

    setIsDisclosureVisible(true);
    const accepted = await new Promise((resolve) => {
      disclosureDecision.current = resolve;
    });
    setIsDisclosureVisible(false);

    if (accepted) {
      await AsyncStorage.setItem(STORAGE_KEY_DISCLOSURE_ACCEPTED, 'true');
    }
    return accepted;
  }, []);

  const acceptDisclosure = useCallback(() => {
    disclosureDecision.current?.(true);
    disclosureDecision.current = null;
  }, []);

  const declineDisclosure = useCallback(() => {
    disclosureDecision.current?.(false);
    disclosureDecision.current = null;
  }, []);

  const requestPermissions = useCallback(async () => {
    const disclosureAccepted = await ensureDisclosureAccepted();
    if (!disclosureAccepted) {
      setErrorMsg('Partage de position refusé');
      setIsPermissionGranted(false);
      return false;
    }

    // Request foreground permissions first
    const { status: foregroundStatus } = await Location.requestForegroundPermissionsAsync();
    if (foregroundStatus !== 'granted') {
      setErrorMsg('Permission de localisation refusée');
      setIsPermissionGranted(false);
      return false;
    }

    // On native platforms, also request background permissions
    if (Platform.OS !== 'web') {
      const backgroundResult = await requestBackgroundPermissions();
      if (!backgroundResult.granted) {
        // Background permission denied, but we can still use foreground tracking
        setErrorMsg('Permission de localisation en arrière-plan refusée');
      }
    }

    setIsPermissionGranted(true);
    return true;
  }, [ensureDisclosureAccepted]);

  const sendLocationUpdate = useCallback(async (coords) => {
    if (!tripId || !coords) return;

    try {
      await updateDriverLocation({
        variables: {
          input: {
            tripId: Number(tripId),
            latitude: coords.latitude,
            longitude: coords.longitude,
          },
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to send location update:', error);
    }
  }, [tripId, updateDriverLocation]);

  const startTracking = useCallback(async () => {
    const hasPermission = await requestPermissions();
    if (!hasPermission) return;

    // Get initial location
    const currentLocation = await Location.getCurrentPositionAsync({
      accuracy: Location.Accuracy.High,
    });
    setLocation(currentLocation.coords);
    sendLocationUpdate(currentLocation.coords);

    // On native platforms, start background tracking
    if (Platform.OS !== 'web') {
      try {
        // Token is already stored in AsyncStorage by AuthContext
        const result = await startBackgroundLocationTracking(tripId);
        if (result.success) {
          setIsBackgroundTracking(true);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to start background tracking:', error);
        // Fall back to foreground-only tracking
      }
    }

    // Also start foreground watching for immediate updates when app is active
    locationSubscription.current = await Location.watchPositionAsync(
      {
        accuracy: Location.Accuracy.High,
        timeInterval: LOCATION_UPDATE_INTERVAL,
        distanceInterval: 50,
      },
      (newLocation) => {
        setLocation(newLocation.coords);
        sendLocationUpdate(newLocation.coords);
      },
    );
  }, [requestPermissions, sendLocationUpdate, tripId]);

  const stopTracking = useCallback(async () => {
    // Stop foreground watching
    if (locationSubscription.current) {
      locationSubscription.current.remove();
      locationSubscription.current = null;
    }

    // Stop background tracking on native platforms
    if (Platform.OS !== 'web') {
      try {
        await stopBackgroundLocationTracking();
        setIsBackgroundTracking(false);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to stop background tracking:', error);
      }
    }
  }, []);

  // Check if background tracking is active on mount
  useEffect(() => {
    if (Platform.OS !== 'web') {
      isBackgroundLocationTrackingActive().then(setIsBackgroundTracking);
    }
  }, []);

  useEffect(() => {
    if (isTracking && tripId) {
      startTracking();
    } else {
      stopTracking();
    }

    return () => {
      // Only stop foreground tracking on unmount
      // Background tracking continues until explicitly stopped
      if (locationSubscription.current) {
        locationSubscription.current.remove();
        locationSubscription.current = null;
      }
    };
  }, [isTracking, tripId, startTracking, stopTracking]);

  return {
    location,
    errorMsg,
    isPermissionGranted,
    isBackgroundTracking,
    isDisclosureVisible,
    acceptDisclosure,
    declineDisclosure,
    requestPermissions,
    startTracking,
    stopTracking,
  };
}
